import React, { useContext, useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { FormattedMessage, useIntl } from 'react-intl';
import moment from 'moment';

// Meridian Components
import Box from '@amzn/meridian/box';
import Row from '@amzn/meridian/row';
import Column from '@amzn/meridian/column';
import Text from '@amzn/meridian/text';
import Button from '@amzn/meridian/button';
import Checkbox from '@amzn/meridian/checkbox';
import Textarea from '@amzn/meridian/textarea';
import Loader from '@amzn/meridian/loader';
import Alert from '@amzn/meridian/alert';

// Custom Hooks
import { useFormTemplate } from '../../../hooks/forms/useFormTemplate';
import { useContactList } from '../../../hooks/forms/useContactList';
import { useFormState } from '../../../hooks/forms/useFormState';
import { useFormValidation } from '../../../hooks/forms/useFormValidation';
import { useFormSubmission } from '../../../hooks/forms/useFormSubmission';
import { useToaster } from '../../../Util/AppToaster';

// Types
import { EmptyProfile, ExtendedUser, Profile } from '../../../Types';
import DataState from '../../../Types/DataState';
import {
  DropdownItem,
  Section,
  Question,
  AnsweredQuestionItem,
  PageItem,
  StepperBulletPointConfig,
} from '../../../Util/Form/formData.types';
import {
  ContactDetailsInput,
  ContactViewTabObject,
  I18NContent,
} from '../../../api/CoachingNotes/schema';
import { ContactStatus } from '../../Dashboard/DashboardComponents/HmdDetails/types';

// Constants
import {
  FormSaveType,
  FORM_IDS,
  SECTION_IDS,
  QUESTION_IDS,
  INGRESS_SOURCES,
  isOutlierIngress,
  isRealtimeHMDIngress,
  TRANSLATION_IDS,
  TOAST_MESSAGES,
} from '../../../Util/Form/formData.constants';
import { FORM_DISPLAY_NAMES } from '../../../Util/Form/formSectionConstants';

// Utilities
import { renderUrl, urls } from '../../../config/Url';
import { StateContext } from '../../../GlobalStateStore';
import { getCurrentWeek, getCurrentWeekBoundriesList } from '../../../Util';
import { getContactListDateRange } from '../../../Util/DateHelper';
import DataSource from '../../../api';
import {
  grabExtraFieldByID,
  isFormReviewedOrDelivered,
} from '../../../Util/Form/helpers';
import {
  processTemplateData,
  applySpecialDropdownRules,
  csaFields as buildCsaFields,
} from '../../../Util/Form/dartFormProcessors';
import {
  fetchContactStatus,
  getFormClickstreamTag1AndTag2,
} from '../utils/helper';
import { EmptyContactStatus } from '../../Dashboard/DashboardComponents/HmdDetails/helper';

// Components
import RenderDropdownsFromData from '../../../Util/Form/RenderDropdownsFromData';
import ContactReviewFeedbackModal from '../../ContactReview/Modal/FeedbackModal/ContactReviewFeedbackModal';
import DartFormTable from './DartFormTable';

// Styles
import { styles } from './DartForm.styles';

// ============================================
// INTERFACES
// ============================================

interface DartReviewFormProps {
  userId: string | undefined;
  contactId: string | undefined;
  selectedVertical: string;
  formType: string;
  ccxDetailsInfo: any;
  ingress: string | null;
  passedProfile?: ExtendedUser;
}

interface ReviewBarItem {
  meetsBar: I18NContent[];
  doesntMeetBar: I18NContent[];
  notApplicable: I18NContent[];
}

interface FormInformation {
  reviewBarCriteria: Record<string, ReviewBarItem>;
  stepperBulletPoints: Record<string, StepperBulletPointConfig>;
  defaultDropdownItems: DropdownItem[];
  tableHeaders: I18NContent[];
}

// ============================================
// MAIN COMPONENT
// ============================================

const DartForm: React.FC<DartReviewFormProps> = ({
  userId,
  contactId,
  selectedVertical,
  formType,
  ccxDetailsInfo,
  passedProfile,
  ingress,
}) => {
  const navigate = useNavigate();
  const intl = useIntl();
  const globalState = useContext(StateContext);

  const { inspireOptions } = globalState?.state;
  const { cscEntityPrefix, isAdmin, requesterUserID } = inspireOptions.data;
  const apiNaws = DataSource.getDataFeed('NAWS');

  // ============================================
  // STATE
  // ============================================

  const initialProfile = passedProfile
    ? { ...EmptyProfile, user: passedProfile }
    : EmptyProfile;
  const [profile, setProfile] = useState<Profile>(initialProfile);
  const [emailChecked, setEmailChecked] = useState(false);
  const [contactSummary, setContactSummary] = useState('');
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [creationTimestamp, setCreationTimestamp] = useState<
    string | undefined
  >();
  const [contactStatusDetails, setContactStatusDetails] =
    useState<ContactStatus>(EmptyContactStatus);

  // Toast notifications
  const { openToast } = useToaster();

  const [formInformation, setFormInformation] = useState<
    Partial<FormInformation>
  >({});
  const [stepperKeys, setStepperKeys] = useState<string[]>([]);

  // ============================================
  // APOLLO CLIENT DATA FETCHING & CUSTOM HOOKS
  // ============================================

  // Hook 1: Form Template
  const {
    templateData,
    loading: templateLoading,
    error: templateError,
  } = useFormTemplate({
    formId:
      formType === FORM_DISPLAY_NAMES.DART_ESCALATIONS_FORM
        ? FORM_IDS.DART_ESCALATIONS
        : FORM_IDS.DART_CONSULTATION,
    version: '1',
    skip:
      !userId || !selectedVertical || inspireOptions.state !== DataState.LOADED,
  });

  // Hook 2: Contact List Management
  const { startWeek, endWeek } = useMemo(() => getContactListDateRange(), []);

  const {
    contacts,
    error: contactsError,
    getContactById,
  } = useContactList({
    userID: userId ? `${cscEntityPrefix}:${userId}` : '',
    userName: profile.user.userName,
    startWeek,
    endWeek,
    skip: !profile.user.userName,
    // Realtime HMD support
    contactId,
    ingress,
    profile,
    cscEntityPrefix,
    contactStatusDetails,
  });

  // Hook 3: Form State Management
  const { state, updateDropdown, updateQuestion, setDropdowns, setPages } =
    useFormState();

  // Hook 4: Form Validation
  const validation = useFormValidation(state);

  // Hook 5: Form Submission
  const {
    submitForm,
    loading: isSubmitting,
    error: submissionError,
  } = useFormSubmission({
    formType,
    ingress,
    profile,
    cscEntityPrefix,
    requesterUserID,
  });

  // ============================================
  // EFFECTS (Optimized to 5)
  // ============================================

  // Effect 1: Fetch profile data (keep for initial profile load)
  useEffect(() => {
    if (inspireOptions.state === DataState.LOADED && userId && !passedProfile) {
      const queryMigrationVariables = apiNaws.prepareQuery(
        getCurrentWeek(),
        userId,
        cscEntityPrefix
      );
      apiNaws.getProfileData(queryMigrationVariables, setProfile, () => {});
    }
  }, [inspireOptions.state, userId, passedProfile]);

  // Effect 2: Fetch contact status details
  useEffect(() => {
    if (ingress && profile.user.userName) {
      const date = getCurrentWeekBoundriesList();
      fetchContactStatus(
        ingress,
        profile,
        cscEntityPrefix,
        date,
        apiNaws,
        setContactStatusDetails,
        () => {}
      );
    }
  }, [ingress, profile.user.userName]);

  // Effect 3: Initialize form from template data
  useEffect(() => {
    if (!templateData) return;

    const processed = processTemplateData(templateData);
    setFormInformation(processed);

    if (processed.stepperBulletPoints) {
      setStepperKeys(Object.keys(processed.stepperBulletPoints));
    }

    if (processed.defaultDropdownItems) {
      const dropdownsWithRules = applySpecialDropdownRules(
        processed.defaultDropdownItems,
        formType,
        selectedVertical
      );
      setDropdowns(dropdownsWithRules);
    }
  }, [templateData, formType]);

  // Effect 4: Load draft data if available
  useEffect(() => {
    if (!contactId || !contacts.length || !templateData) return;

    const contactInfo = getContactById(contactId);
    if (!contactInfo) return;

    if (contactInfo.contactInfo === null) {
      setCreationTimestamp(new Date().toISOString());
    }

    const formReviews = contactInfo.formReviews?.filter(f =>
      f.formID.startsWith('DART')
    );
    if (!formReviews?.[0]) return;

    const questionsAnswered = formReviews[0].formAnswer
      .filter(
        answer => !answer.questionID.includes(QUESTION_IDS.MANAGER_SUMMARY)
      )
      .reduce(
        (acc, answer) => {
          // Note: 'value' exists in API response but not in TypeScript schema
          // This is a known schema limitation - safe to cast
          const answerWithValue = answer as any;
          acc[answer.questionText.defaultMessage] = {
            comments: answer.answerText || '',
            checkedValue: answerWithValue.value,
          };
          return acc;
        },
        {} as Record<string, AnsweredQuestionItem>
      );

    const dropdownQuestions =
      templateData.SECTIONS?.find(
        (s: Section) => s.SECTION_ID === SECTION_IDS.DART_DROPDOWNS
      )?.QUESTIONS ?? [];

    const dropdownItems = formReviews[0].formAnswer
      .filter(answer =>
        dropdownQuestions.some(
          (q: Question) => q.QUESTION_ID === answer.questionID
        )
      )
      .map(answer => ({
        type: answer.questionID.toLowerCase(),
        placeholder: {
          TRANSLATION_ID: answer.questionText.translationID,
          DEFAULT_MESSAGE: answer.questionText.defaultMessage,
        },
        options:
          dropdownQuestions.find(
            (q: Question) =>
              q.QUESTION_TEXT.TRANSLATION_ID ===
              answer.questionText.translationID
          )?.QUESTION_OPTIONS ?? [],
        selected: answer.selectedOption?.defaultMessage,
      }));

    const summaryAnswer = formReviews[0].formAnswer.find(f =>
      f.questionID.includes(QUESTION_IDS.MANAGER_SUMMARY)
    );
    if (summaryAnswer?.answerText) {
      setContactSummary(summaryAnswer.answerText);
    }

    // Update form state through hooks - merge and apply special rules
    if (dropdownItems.length > 0) {
      // Merge draft data with existing, then apply special rules to ensure vertical is set
      const mergedDropdowns = state.dropdownItems.map(existingItem => {
        const draftItem = dropdownItems.find(d => d.type === existingItem.type);
        if (!draftItem) return existingItem;

        // Preserve non-null values from draft
        return {
          ...draftItem,
          selected: draftItem.selected ?? existingItem.selected,
        };
      });

      // Apply special rules to ensure vertical and formType are always set
      const dropdownsWithRules = applySpecialDropdownRules(
        mergedDropdowns,
        formType,
        selectedVertical
      );
      setDropdowns(dropdownsWithRules);
    }

    // Update questions answered
    Object.entries(questionsAnswered).forEach(([key, value]) => {
      updateQuestion(key, value);
    });
  }, [contactId, contacts, templateData, formType]);

  // Effect 5: Initialize pages from stepper keys
  useEffect(() => {
    if (!formInformation.stepperBulletPoints) return;

    const pages: PageItem[] = [];
    stepperKeys.forEach(key => {
      const points = formInformation.stepperBulletPoints?.[key]?.points;
      if (points) {
        pages.push({
          points: points.map(point => ({ text: point })),
          type: key,
        });
      }
    });

    setPages(pages);
  }, [formInformation, stepperKeys]);

  // ============================================
  // EVENT HANDLERS
  // ============================================

  const handleSelectDropdownValue = (type: string) => (selected: string) => {
    updateDropdown(type, selected);
  };

  const handleQuestionAnswered =
    (item: string) => (data: AnsweredQuestionItem) => {
      updateQuestion(item, data);
    };

  const handleSaveAsDraft = async () => {
    if (!currentContact || !templateData) return;

    try {
      await submitForm({
        formState: state,
        templateData,
        contact: currentContact,
        feedbackDetails: undefined,
        saveType: FormSaveType.DRAFT,
        emailChecked,
        contactSummary,
        creationTimestamp,
      });

      openToast({
        children: `${TOAST_MESSAGES.DRAFT_SUCCESS}`,
        alertType: 'success',
        timeout: 3000,
      });

      // Navigate after draft save
      let redirectUrl = `${urls.contactReview}/${userId}/${contactId}`;
      const urlParams = new URLSearchParams(window.location.search);

      if (isRealtimeHMDIngress(ingress)) {
        urlParams.set('ingress', INGRESS_SOURCES.REALTIME_HMD);
      }
      if (isOutlierIngress(ingress)) {
        urlParams.set('ingress', INGRESS_SOURCES.OUTLIER);
      }
      redirectUrl += `?${urlParams}`;
      navigate(renderUrl(redirectUrl), { replace: true });
    } catch (error) {
      console.error('Error saving draft:', error);
      openToast({
        children: submissionError?.message || `${TOAST_MESSAGES.ERROR_SAVING}`,
        alertType: 'error',
        timeout: 3000,
      });
    }
  };

  const handleSave = async (feedbackDetails: ContactDetailsInput) => {
    if (!currentContact || !templateData) return;

    try {
      await submitForm({
        formState: state,
        templateData,
        contact: currentContact,
        feedbackDetails,
        saveType: FormSaveType.DELIVERED,
        emailChecked,
        contactSummary,
        creationTimestamp,
      });

      setShowFeedbackModal(false);

      // Navigate after successful submission
      const redirectUrl = isOutlierIngress(ingress)
        ? urls.outlierManagement
        : `${urls.coachingNotes}/${userId}?href=${ContactViewTabObject.REVIEWED}${
            isRealtimeHMDIngress(ingress)
              ? `&ingress=${INGRESS_SOURCES.REALTIME_HMD}`
              : ''
          }`;

      setTimeout(() => {
        navigate(renderUrl(redirectUrl), { replace: true });
      }, 3000);
    } catch (error) {
      console.error('Error submitting form:', error);
      openToast({
        children:
          submissionError?.message || `${TOAST_MESSAGES.ERROR_SUBMITTING}`,
        alertType: 'error',
        timeout: 3000,
      });
    }
  };

  const handleFeedbackSubmitted = async (
    feedbackDetails: ContactDetailsInput
  ) => {
    await handleSave(feedbackDetails);
  };

  // ============================================
  // DERIVED STATE & VALIDATION
  // ============================================

  const currentContact = useMemo(
    () => (contactId ? getContactById(contactId) : undefined),
    [contactId, getContactById]
  );

  const isReviewedOrDelivered = useMemo(
    () => isFormReviewedOrDelivered(contactId, currentContact?.contactStatus),
    [contactId, currentContact?.contactStatus]
  );

  const isSaveDraftButtonDisabled = isReviewedOrDelivered || isSubmitting;
  const isDeliverButtonDisabled =
    !validation.canSave || isReviewedOrDelivered || isSubmitting;
  const isEmailCheckBoxDisabled = isDeliverButtonDisabled;

  // ============================================
  // RENDER
  // ============================================

  if (templateLoading) {
    return (
      <div style={{ textAlign: 'center' }}>
        <Loader data-testid='loader' type='circular' size='large' />
      </div>
    );
  }

  // Show errors via toast and display loader (allows user to retry)
  if (templateError) {
    openToast({
      children: `${TOAST_MESSAGES.FORM_TEMPLATE_FAILED}: ${templateError.message}`,
      alertType: 'error',
      timeout: 5000,
    });
    return (
      <div style={{ textAlign: 'center' }}>
        <Loader data-testid='loader' type='circular' size='large' />
      </div>
    );
  }

  if (contactsError) {
    openToast({
      children: `${TOAST_MESSAGES.CONTACTS_FAILED}: ${contactsError.message}`,
      alertType: 'error',
      timeout: 5000,
    });
    return (
      <div style={{ textAlign: 'center' }}>
        <Loader data-testid='loader' type='circular' size='large' />
      </div>
    );
  }

  if (!templateData) {
    return <Loader data-testid='loader' type='circular' size='large' />;
  }

  // Use csaFields from dartFormProcessors for button tracking
  const csaFields = (
    type: string,
    subtype: string,
    contentID: string,
    ingressParam: string | null,
    isCCX?: boolean
  ) =>
    buildCsaFields(
      type,
      subtype,
      contentID,
      ingressParam,
      getFormClickstreamTag1AndTag2,
      isCCX
    );

  // ============================================
  // MAIN JSX RENDER
  // ============================================

  return (
    <>
      <Box className={styles.dartFormContainer}>
        <Column
          alignmentVertical='top'
          className={styles.radiodropdownParent}
          spacing='500'
        >
          <Row widths={['grid-3', 'grid-9']} alignmentVertical='top'>
            <Box>
              <RenderDropdownsFromData
                data={templateData}
                formPrefix=''
                dropdownSectionID={SECTION_IDS.DART_DROPDOWNS}
                styles={styles}
                handleSelectDropdownValue={handleSelectDropdownValue}
                formState={state}
                setFormState={setDropdowns}
              />
            </Box>
          </Row>

          <Row className={styles.title}>
            <Text type='h400'>
              <FormattedMessage
                id={
                  formType === FORM_DISPLAY_NAMES.DART_ESCALATIONS_FORM
                    ? TRANSLATION_IDS.DART_ESCALATIONS_TITLE
                    : TRANSLATION_IDS.DART_CONSULTATION_TITLE
                }
                defaultMessage={formType}
              />
            </Text>
          </Row>
        </Column>

        {formType && (
          <>
            <Column width='100%'>
              {formInformation.tableHeaders && (
                <DartFormTable
                  tableHeaders={formInformation.tableHeaders}
                  pages={state.pages}
                  questionsAnswered={state.questionsAnswered}
                  reviewBarCriteria={formInformation.reviewBarCriteria ?? {}}
                  isReviewedOrDelivered={isReviewedOrDelivered}
                  ingress={ingress}
                  ccxDetailsInfo={ccxDetailsInfo}
                  onQuestionAnswered={handleQuestionAnswered}
                />
              )}
            </Column>

            <Column
              spacingInset='300 300'
              spacing='400'
              width='100%'
              type='outline'
              className={styles.contactSummary}
            >
              <Text type='b400'>
                <div style={{ color: '#3690A1' }}>
                  <FormattedMessage
                    id='cs_perfportal_dart_escalations_manager_summary'
                    defaultMessage='Manager Summary'
                  />
                </div>
              </Text>
              <Textarea
                value={contactSummary}
                disabled={isReviewedOrDelivered}
                onChange={setContactSummary}
                placeholder={intl.formatMessage({
                  id: 'cs_perfportal_contact_review_label_feedback_note',
                  defaultMessage: 'Additional notes or comments? (Optional)',
                })}
                width='100%'
                error={false}
                helperText=''
              />
            </Column>

            <Column className={styles.buttonsContainer}>
              <Row alignmentHorizontal='center'>
                <Button
                  onClick={handleSaveAsDraft}
                  type='secondary'
                  className={styles.saveButton}
                  disabled={isSaveDraftButtonDisabled}
                  {...csaFields(
                    'button',
                    'save as draft',
                    'cs_perfportal_tx_monitoring_draft',
                    ingress,
                    !!ccxDetailsInfo
                  )}
                >
                  <FormattedMessage
                    id='cs_perfportal_tx_monitoring_draft'
                    defaultMessage={
                      grabExtraFieldByID(
                        templateData as any,
                        'cs_perfportal_tx_monitoring_draft'
                      )?.DEFAULT_MESSAGE ?? 'Save as Draft'
                    }
                  />
                </Button>
                <Button
                  data-testid='deliver-button'
                  onClick={() => setShowFeedbackModal(true)}
                  className={styles.saveButton}
                  disabled={isDeliverButtonDisabled}
                  {...csaFields(
                    'button',
                    'deliver',
                    'cs_perfportal_tx_monitoring_deliver',
                    ingress,
                    !!ccxDetailsInfo
                  )}
                >
                  <FormattedMessage
                    id='cs_perfportal_tx_monitoring_deliver'
                    defaultMessage={
                      grabExtraFieldByID(
                        templateData as any,
                        'cs_perfportal_tx_monitoring_deliver'
                      )?.DEFAULT_MESSAGE ?? 'Deliver'
                    }
                  />
                </Button>
              </Row>
              <Row
                className={styles.emailCheckboxContainer}
                alignmentHorizontal='center'
              >
                <Checkbox
                  checked={emailChecked}
                  onChange={setEmailChecked}
                  disabled={isEmailCheckBoxDisabled}
                  data-csa-c-type='element'
                  data-csa-c-slot-id='tx-monitoring-email-notification-slot'
                  data-csa-c-content-id='tx-monitoring-email-notification-checkbox'
                  data-csa-c-checked={`${emailChecked}`}
                >
                  <FormattedMessage
                    id='cs_perfportal_tx-monitoring_email_notification'
                    defaultMessage='E-mail summary to associate'
                  />
                </Checkbox>
              </Row>
            </Column>
          </>
        )}
      </Box>

      {contactId && currentContact && (
        <ContactReviewFeedbackModal
          open={showFeedbackModal}
          parentValues={currentContact}
          parentUserName={profile?.user?.userName}
          parentUserID={userId ?? ''}
          onFeedbackSubmitted={handleFeedbackSubmitted}
          contactTab={ContactViewTabObject.RECOMMENDED}
          contactCategory='Corrective'
          ingress={ingress}
        />
      )}
    </>
  );
};

export default DartForm;
